import { useMemo, useState, type FormEvent } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AssignmentTurnedIn,
  CheckCircle,
  ErrorOutline,
  FactCheck,
  PlaylistAddCheck,
  PriceCheck,
  ReceiptLong,
  Save,
  UploadFile,
} from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as vatApi from '@/api/vatPeriods';
import { listCuitProfiles } from '@/api/cuitProfiles';
import { toApiError } from '@/api/client';
import type { IvaRate } from '@/api/invoices';
import { EmptyState } from '@/components/EmptyState';
import { LoadingScreen } from '@/components/LoadingScreen';
import { EntitlementGate } from '@/components/primitives/EntitlementGate';
import { useProduct } from '@/auth/ProductContext';
import { useCurrentCuit } from '@/auth/CuitContext';
import type { TildeProduct } from '@/components/primitives/products';
import { usePermissions } from '@/hooks/usePermissions';
import type { Permissions } from '@/hooks/usePermissions';
import { IVA_RATE_LABEL } from '@/lib/labels';
import { formatCurrency } from '@/lib/formatters';

export const Route = createFileRoute('/_authenticated/iva/')({
  component: VatPeriodsPage,
});

const IVA_RATES: IvaRate[] = ['Rate21', 'Rate105', 'Rate27', 'Rate5', 'Rate25', 'Rate0'];
const PURCHASE_KIND_LABEL: Record<vatApi.VatPurchaseDocumentKind, string> = {
  Invoice: 'Factura',
  CreditNote: 'Nota de crédito',
  DebitNote: 'Nota de débito',
  Import: 'Importación',
};
const CREDIT_TREATMENT_LABEL: Record<vatApi.VatCreditFiscalTreatment, string> = {
  PendingReview: 'Pendiente revisión',
  Computable: 'Computable',
  PartiallyComputable: 'Parcialmente computable',
  NonComputable: 'No computable',
};
const ADJUSTMENT_KIND_LABEL: Record<vatApi.VatAdjustmentKind, string> = {
  Withholding: 'Retención',
  Perception: 'Percepción',
  PaymentOnAccount: 'Pago a cuenta',
  CreditFiscal: 'Crédito fiscal manual',
  DebitFiscal: 'Débito fiscal manual',
  PreviousCreditBalance: 'Saldo a favor anterior',
};
const TAX_LABEL: Record<vatApi.VatTaxKind, string> = {
  Iva: 'IVA',
  Ganancias: 'Ganancias',
  Iibb: 'IIBB',
  Other: 'Otro',
};

interface PurchaseFormState {
  issueDate: string;
  supplierCuit: string;
  supplierName: string;
  kind: vatApi.VatPurchaseDocumentKind;
  pointOfSale: string;
  documentNumber: string;
  currency: string;
  exchangeRate: string;
  totalAmount: string;
  nonTaxedAmount: string;
  exemptAmount: string;
  creditFiscalTreatment: vatApi.VatCreditFiscalTreatment;
  creditFiscalPercentage: string;
  rate: IvaRate;
  baseAmount: string;
  vatAmount: string;
  externalReference: string;
}

interface AdjustmentFormState {
  effectiveDate: string;
  kind: vatApi.VatAdjustmentKind;
  tax: vatApi.VatTaxKind;
  amount: string;
  taxBase: string;
  rate: string;
  certificateNumber: string;
  description: string;
  externalReference: string;
}

function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function firstDay(period: string): string {
  return `${period}-01`;
}

function toNumber(value: string, fallback = 0): number {
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function optionalNumber(value: string): number | null {
  const trimmed = value.trim();
  return trimmed ? toNumber(trimmed) : null;
}

export function canAccessVatRi(
  permissions: Pick<Permissions, 'canViewInvoices'>,
  activeProduct: TildeProduct,
  activeCommercialProduct: string,
): boolean {
  return permissions.canViewInvoices && activeProduct === 'Empresa' && activeCommercialProduct === 'ResponsableInscripto';
}

function formatPeriodLabel(period: string): string {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return period;
  const date = new Date(year, month - 1, 1);
  return new Intl.DateTimeFormat('es-AR', { month: 'long', year: 'numeric' }).format(date);
}

function defaultPurchase(period: string): PurchaseFormState {
  return {
    issueDate: firstDay(period),
    supplierCuit: '',
    supplierName: '',
    kind: 'Invoice',
    pointOfSale: '1',
    documentNumber: '',
    currency: 'ARS',
    exchangeRate: '1',
    totalAmount: '',
    nonTaxedAmount: '0',
    exemptAmount: '0',
    creditFiscalTreatment: 'Computable',
    creditFiscalPercentage: '100',
    rate: 'Rate21',
    baseAmount: '',
    vatAmount: '',
    externalReference: '',
  };
}

function defaultAdjustment(period: string): AdjustmentFormState {
  return {
    effectiveDate: firstDay(period),
    kind: 'Withholding',
    tax: 'Iva',
    amount: '',
    taxBase: '',
    rate: '',
    certificateNumber: '',
    description: '',
    externalReference: '',
  };
}

function VatPeriodsPage(): JSX.Element {
  const qc = useQueryClient();
  const perms = usePermissions();
  const { activeProduct, activeCommercialProduct } = useProduct();
  const { currentCuitProfileId } = useCurrentCuit();
  const isResponsableInscripto = canAccessVatRi(perms, activeProduct, activeCommercialProduct);
  const canAccessVatWorkspace = perms.canViewInvoices && isResponsableInscripto;
  const profilesQ = useQuery({
    queryKey: ['cuit-profiles', 'vat-scope'],
    queryFn: () => listCuitProfiles(0, 100),
    enabled: canAccessVatWorkspace,
  });
  const activeProfile = profilesQ.data?.items.find((profile) => profile.id === currentCuitProfileId && profile.status === 'Active');
  const fiscalScope = activeProfile ? {
    cuitProfileId: activeProfile.id,
    issuerCuit: activeProfile.cuit,
    environment: activeProfile.environment,
  } satisfies vatApi.VatFiscalScope : null;
  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod());
  const [purchase, setPurchase] = useState(() => defaultPurchase(currentPeriod()));
  const [adjustment, setAdjustment] = useState(() => defaultAdjustment(currentPeriod()));
  const [csv, setCsv] = useState('');
  const [exported, setExported] = useState<vatApi.VatPeriodExport | null>(null);
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);

  const periodsQ = useQuery({
    queryKey: ['vat-periods', fiscalScope],
    queryFn: () => vatApi.listVatPeriods(fiscalScope!, 24),
    enabled: Boolean(canAccessVatWorkspace && fiscalScope),
  });
  const periodQ = useQuery({
    queryKey: ['vat-period', fiscalScope, selectedPeriod],
    queryFn: () => vatApi.getVatPeriod(fiscalScope!, selectedPeriod),
    enabled: Boolean(canAccessVatWorkspace && fiscalScope),
    retry: false,
  });

  const periodNotCreated = periodQ.error && toApiError(periodQ.error).status === 404;
  const view = periodQ.data;
  const isClosed = view?.status === 'Closed';
  const summary = view?.summary;
  const hasAlerts = (view?.alerts.length ?? 0) > 0;
  const hasActionRequired = view?.alerts.some((alert) => alert.actionRequired) ?? false;
  const canExport = Boolean(view && !periodNotCreated);
  const readinessItems = [
    {
      label: 'Ventas autorizadas',
      detail: `${summary?.authorizedSalesCount ?? 0} comprobante(s) incluidos en el débito fiscal`,
      done: Boolean((summary?.authorizedSalesCount ?? 0) > 0),
      tone: 'info' as const,
    },
    {
      label: 'Compras cargadas',
      detail: `${summary?.purchaseCount ?? 0} compra(s) cargada(s) para crédito fiscal`,
      done: Boolean((summary?.purchaseCount ?? 0) > 0),
      tone: 'warning' as const,
    },
    {
      label: 'Créditos sin revisar',
      detail: `${summary?.pendingReviewCount ?? 0} documento(s) pendiente(s)`,
      done: Boolean(view && (summary?.pendingReviewCount ?? 0) === 0),
      tone: 'warning' as const,
    },
    {
      label: 'Alertas del período',
      detail: hasAlerts ? `${view?.alerts.length ?? 0} alerta(s) para mirar antes de cerrar` : 'Sin alertas activas',
      done: Boolean(view && !hasActionRequired),
      tone: hasActionRequired ? 'error' as const : 'info' as const,
    },
  ];

  const invalidate = async (period: string): Promise<void> => {
    setExported(null);
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['vat-periods', fiscalScope] }),
      qc.invalidateQueries({ queryKey: ['vat-period', fiscalScope, period] }),
    ]);
  };

  const addPurchaseMut = useMutation({
    mutationFn: () => {
      const lines = purchase.baseAmount || purchase.vatAmount
        ? [{ rate: purchase.rate, baseAmount: toNumber(purchase.baseAmount), vatAmount: toNumber(purchase.vatAmount) }]
        : [];
      return vatApi.addVatPurchase(fiscalScope!, selectedPeriod, {
        issueDate: purchase.issueDate,
        supplierCuit: purchase.supplierCuit.replace(/\D/g, ''),
        supplierName: purchase.supplierName,
        kind: purchase.kind,
        pointOfSale: Math.trunc(toNumber(purchase.pointOfSale, 0)),
        documentNumber: purchase.documentNumber,
        currency: purchase.currency,
        exchangeRate: toNumber(purchase.exchangeRate, 1),
        totalAmount: toNumber(purchase.totalAmount),
        nonTaxedAmount: toNumber(purchase.nonTaxedAmount),
        exemptAmount: toNumber(purchase.exemptAmount),
        creditFiscalTreatment: purchase.creditFiscalTreatment,
        creditFiscalPercentage: toNumber(purchase.creditFiscalPercentage),
        ivaBreakdown: lines,
        externalReference: purchase.externalReference.trim() || null,
      });
    },
    onSuccess: async (result) => {
      setToast({ severity: 'success', message: 'Compra cargada en el período IVA.' });
      setPurchase(defaultPurchase(result.period));
      await invalidate(result.period);
    },
    onError: (err) => setToast({ severity: 'error', message: toApiError(err).message }),
  });

  const importMut = useMutation({
    mutationFn: () => vatApi.importVatPurchases(fiscalScope!, selectedPeriod, csv),
    onSuccess: async (result) => {
      setToast({ severity: 'success', message: `Importadas ${result.added} compra(s).` });
      setCsv('');
      await invalidate(result.period.period);
    },
    onError: (err) => setToast({ severity: 'error', message: toApiError(err).message }),
  });

  const adjustmentMut = useMutation({
    mutationFn: () => vatApi.addVatAdjustment(fiscalScope!, selectedPeriod, {
      effectiveDate: adjustment.effectiveDate,
      kind: adjustment.kind,
      tax: adjustment.tax,
      amount: toNumber(adjustment.amount),
      taxBase: optionalNumber(adjustment.taxBase),
      rate: optionalNumber(adjustment.rate),
      certificateNumber: adjustment.certificateNumber.trim() || null,
      description: adjustment.description.trim() || null,
      externalReference: adjustment.externalReference.trim() || null,
    }),
    onSuccess: async (result) => {
      setToast({ severity: 'success', message: 'Ajuste registrado en el período IVA.' });
      setAdjustment(defaultAdjustment(result.period));
      await invalidate(result.period);
    },
    onError: (err) => setToast({ severity: 'error', message: toApiError(err).message }),
  });

  const closeMut = useMutation({
    mutationFn: () => vatApi.closeVatPeriod(fiscalScope!, selectedPeriod),
    onSuccess: async (result) => {
      setToast({ severity: 'success', message: `Período ${result.period} cerrado.` });
      await invalidate(result.period);
    },
    onError: (err) => setToast({ severity: 'error', message: toApiError(err).message }),
  });

  const exportMut = useMutation({
    mutationFn: () => vatApi.exportVatPeriod(fiscalScope!, selectedPeriod),
    onSuccess: (result) => {
      setExported(result);
      setToast({ severity: 'success', message: 'Export IVA generado.' });
    },
    onError: (err) => setToast({ severity: 'error', message: toApiError(err).message }),
  });

  const sampleCsv = useMemo(
    () =>
      'issueDate,supplierCuit,supplierName,kind,pointOfSale,documentNumber,currency,exchangeRate,totalAmount,nonTaxedAmount,exemptAmount,creditFiscalTreatment,creditFiscalPercentage,rate21Base,rate21Vat,externalReference\\n' +
      `${firstDay(selectedPeriod)},30711222333,Proveedor SA,Invoice,1,00000042,ARS,1,1210,0,0,Computable,100,1000,210,oc-42`,
    [selectedPeriod],
  );

  const onChangePeriod = (value: string): void => {
    setSelectedPeriod(value);
    setPurchase(defaultPurchase(value));
    setAdjustment(defaultAdjustment(value));
    setExported(null);
  };

  if (periodsQ.isLoading && !periodQ.data && !periodQ.error) {
    return <LoadingScreen message="Cargando workspace IVA…" />;
  }

  return (
    <EntitlementGate
      allowed={canAccessVatWorkspace}
      reason="plan"
      title="IVA RI es un producto para Responsables Inscriptos"
      description="Tu cuenta no tiene activo el producto Responsable Inscripto, así que no mostramos posición IVA ni cierre mensual de RI. Si cambiás de régimen, activá ese producto."
    >
      <Box>
      {!fiscalScope ? <MuiAlert severity="warning" sx={{ mb: 2 }}>Seleccioná un CUIT activo para trabajar el IVA sin mezclar contribuyentes ni ambientes.</MuiAlert> : null}
      {toast ? <MuiAlert severity={toast.severity} sx={{ mb: 2 }} onClose={() => setToast(null)}>{toast.message}</MuiAlert> : null}

      <PeriodHero
        selectedPeriod={selectedPeriod}
        view={view}
        periodNotCreated={Boolean(periodNotCreated)}
        isClosed={isClosed}
        isExporting={exportMut.isPending}
        isClosing={!fiscalScope || closeMut.isPending}
        onChangePeriod={onChangePeriod}
        onExport={() => exportMut.mutate()}
        onClosePeriod={() => closeMut.mutate()}
      />

      {periodQ.error && !periodNotCreated ? (
        <MuiAlert severity="error" sx={{ mb: 2 }}>{toApiError(periodQ.error).message}</MuiAlert>
      ) : null}

      <Stack direction="row" gap={1} sx={{ mb: 2 }} flexWrap="wrap">
        {(periodsQ.data?.items ?? []).map((p) => (
          <Chip
            key={p.period}
            label={`${p.period} · ${p.status === 'Closed' ? 'cerrado' : 'abierto'}`}
            color={p.status === 'Closed' ? 'default' : 'primary'}
            variant={p.period === selectedPeriod ? 'filled' : 'outlined'}
            onClick={() => onChangePeriod(p.period)}
          />
        ))}
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Stack spacing={2}>
            <CloseReadinessCard
              selectedPeriod={selectedPeriod}
              view={view}
              periodNotCreated={Boolean(periodNotCreated)}
              items={readinessItems}
            />
            <Grid container spacing={2}>
              <Kpi label="Débito por ventas" value={formatCurrency(summary?.salesVat ?? 0, 'ARS')} hint={`${summary?.authorizedSalesCount ?? 0} venta(s) autorizada(s)`} />
              <Kpi label="Crédito por compras" value={formatCurrency(summary?.computableInputVat ?? 0, 'ARS')} hint={`${summary?.purchaseCount ?? 0} compra(s)`} />
              <Kpi label="Retenciones y saldos" value={formatCurrency(summary?.vatOffsets ?? 0, 'ARS')} hint={`${summary?.adjustmentCount ?? 0} ajuste(s)`} />
              <Kpi
                label="Saldo operativo"
                value={formatCurrency(summary?.finalBalance ?? 0, 'ARS')}
                hint={(summary?.estimatedPayable ?? 0) > 0 ? 'preparar pago' : 'saldo a favor'}
              />
            </Grid>
            <PeriodDetail view={view} />
          </Stack>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Stack spacing={2}>
            <OperatorActionsCard
              canExport={canExport}
              isClosed={isClosed}
              onExport={() => exportMut.mutate()}
              onClosePeriod={() => closeMut.mutate()}
              exportDisabled={!canExport || exportMut.isPending}
              closeDisabled={!fiscalScope || isClosed || closeMut.isPending}
            />
            <PurchaseForm
              form={purchase}
              disabled={!fiscalScope || isClosed || addPurchaseMut.isPending}
              onChange={(patch) => setPurchase((prev) => ({ ...prev, ...patch }))}
              onSubmit={(e) => {
                e.preventDefault();
                addPurchaseMut.mutate();
              }}
            />
            <ImportCsvForm
              csv={csv}
              sampleCsv={sampleCsv}
              disabled={!fiscalScope || isClosed || importMut.isPending}
              onChange={setCsv}
              onSubmit={(e) => {
                e.preventDefault();
                importMut.mutate();
              }}
            />
            <AdjustmentForm
              form={adjustment}
              disabled={!fiscalScope || isClosed || adjustmentMut.isPending}
              onChange={(patch) => setAdjustment((prev) => ({ ...prev, ...patch }))}
              onSubmit={(e) => {
                e.preventDefault();
                adjustmentMut.mutate();
              }}
            />
          </Stack>
        </Grid>
        {exported ? (
          <Grid item xs={12}>
            <ExportPreview exported={exported} />
          </Grid>
        ) : null}
      </Grid>
      </Box>
    </EntitlementGate>
  );
}

function PeriodHero({
  selectedPeriod,
  view,
  periodNotCreated,
  isClosed,
  isExporting,
  isClosing,
  onChangePeriod,
  onExport,
  onClosePeriod,
}: {
  selectedPeriod: string;
  view?: vatApi.VatPeriodView;
  periodNotCreated: boolean;
  isClosed?: boolean;
  isExporting: boolean;
  isClosing: boolean;
  onChangePeriod: (value: string) => void;
  onExport: () => void;
  onClosePeriod: () => void;
}): JSX.Element {
  const summary = view?.summary;
  const payable = summary?.estimatedPayable ?? 0;
  const creditBalance = summary?.estimatedCreditBalance ?? 0;
  const primaryAmount = payable > 0 ? payable : creditBalance;
  const primaryLabel = payable > 0 ? 'A preparar para pago' : 'Saldo a favor estimado';
  const stateLabel = periodNotCreated ? 'Borrador pendiente' : isClosed ? 'Cierre congelado' : 'Cierre en preparación';

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        overflow: 'hidden',
        background:
          'linear-gradient(135deg, rgba(124,92,255,.18), rgba(0,191,255,.08) 48%, rgba(255,171,64,.10))',
      }}
    >
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={3}>
          <Stack spacing={1.25} sx={{ maxWidth: 760 }}>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <PriceCheck color="primary" />
              <Typography variant="overline" color="primary.main" sx={{ letterSpacing: 1.1 }}>
                Responsable Inscripto · IVA mensual
              </Typography>
              <Chip
                size="small"
                label={stateLabel}
                color={isClosed ? 'default' : periodNotCreated ? 'warning' : 'success'}
                variant={isClosed ? 'outlined' : 'filled'}
              />
            </Stack>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              Cerrá {formatPeriodLabel(selectedPeriod)} sin perseguir planillas
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Tilde junta ventas autorizadas, compras, retenciones y saldos para que llegues al contador
              con un borrador revisable, trazable y listo para exportar.
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Chip icon={<ReceiptLong />} label={`${summary?.authorizedSalesCount ?? 0} ventas`} variant="outlined" />
              <Chip icon={<FactCheck />} label={`${summary?.purchaseCount ?? 0} compras`} variant="outlined" />
              <Chip icon={<PlaylistAddCheck />} label={`${summary?.pendingReviewCount ?? 0} por revisar`} variant="outlined" />
            </Stack>
          </Stack>

          <Paper variant="outlined" sx={{ p: 2, minWidth: { xs: '100%', md: 310 }, bgcolor: 'background.paper' }}>
            <Typography variant="caption" color="text.secondary">{primaryLabel}</Typography>
            <Typography variant="h4" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 800, mt: .5 }}>
              {formatCurrency(primaryAmount, 'ARS')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Cálculo estimado. La presentación final ante ARCA sigue fuera del MVP.
            </Typography>
            <Stack direction="row" gap={1} sx={{ mt: 2 }}>
              <TextField
                label="Período"
                type="month"
                size="small"
                value={selectedPeriod}
                onChange={(e) => onChangePeriod(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <Button variant="outlined" onClick={onExport} disabled={periodNotCreated || isExporting}>
                Exportar
              </Button>
            </Stack>
            <Button
              fullWidth
              color="warning"
              variant="contained"
              sx={{ mt: 1 }}
              onClick={onClosePeriod}
              disabled={isClosed || isClosing}
            >
              {isClosed ? 'Período cerrado' : 'Cerrar período'}
            </Button>
          </Paper>
        </Stack>
      </Box>
    </Paper>
  );
}

function CloseReadinessCard({
  selectedPeriod,
  view,
  periodNotCreated,
  items,
}: {
  selectedPeriod: string;
  view?: vatApi.VatPeriodView;
  periodNotCreated: boolean;
  items: Array<{ label: string; detail: string; done: boolean; tone: 'info' | 'warning' | 'error' }>;
}): JSX.Element {
  return (
    <Paper variant="outlined" sx={{ p: 2.25 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Tablero de cierre</Typography>
          <Typography variant="body2" color="text.secondary">
            Mirada operativa de lo que falta para entregar el período con confianza.
          </Typography>
        </Box>
        <Chip
          icon={view?.status === 'Closed' ? <CheckCircle /> : <AssignmentTurnedIn />}
          label={view?.status === 'Closed' ? `Cerrado · ${view.closedAt ?? selectedPeriod}` : 'En curso'}
          color={view?.status === 'Closed' ? 'default' : 'primary'}
          variant="outlined"
        />
      </Stack>

      {periodNotCreated ? (
        <MuiAlert severity="warning" sx={{ mb: 2 }}>
          Todavía no hay workspace para {selectedPeriod}. Cargá una compra, importá un CSV o registrá un ajuste para abrirlo.
        </MuiAlert>
      ) : null}

      <Grid container spacing={1.5}>
        {items.map((item) => (
          <Grid item xs={12} sm={6} key={item.label}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                height: '100%',
                borderColor: item.done ? 'success.main' : `${item.tone}.main`,
                bgcolor: item.done ? 'success.main' : `${item.tone}.main`,
                backgroundImage: 'linear-gradient(rgba(8,13,32,.90), rgba(8,13,32,.90))',
              }}
            >
              <Stack direction="row" gap={1.25} alignItems="flex-start">
                {item.done ? <CheckCircle color="success" /> : <ErrorOutline color={item.tone} />}
                <Box>
                  <Typography variant="subtitle2">{item.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.detail}</Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

function OperatorActionsCard({
  canExport,
  isClosed,
  onExport,
  onClosePeriod,
  exportDisabled,
  closeDisabled,
}: {
  canExport: boolean;
  isClosed?: boolean;
  onExport: () => void;
  onClosePeriod: () => void;
  exportDisabled: boolean;
  closeDisabled: boolean;
}): JSX.Element {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1">Qué hacer ahora</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Atajos para avanzar el cierre, no sólo administrar registros.
      </Typography>
      <Stack spacing={1.5}>
        <ActionHint
          icon={<ReceiptLong color="primary" />}
          title="1. Subí compras del mes"
          body="Cargá una factura puntual o pegá el CSV del sistema contable para alimentar el crédito fiscal."
        />
        <ActionHint
          icon={<FactCheck color="primary" />}
          title="2. Sumá retenciones y percepciones"
          body="Registrá certificados, pagos a cuenta o saldos anteriores antes de mirar el saldo final."
        />
        <ActionHint
          icon={<AssignmentTurnedIn color="primary" />}
          title="3. Exportá y cerrá"
          body="Bajá los CSV para revisión y congelá el período cuando el contador lo valide."
        />
        <Stack direction="row" gap={1}>
          <Button variant="outlined" onClick={onExport} disabled={!canExport || exportDisabled} fullWidth>
            Exportar
          </Button>
          <Button color="warning" variant="contained" onClick={onClosePeriod} disabled={closeDisabled} fullWidth>
            {isClosed ? 'Cerrado' : 'Cerrar'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function ActionHint({ icon, title, body }: { icon: JSX.Element; title: string; body: string }): JSX.Element {
  return (
    <Stack direction="row" gap={1.25} alignItems="flex-start">
      <Box sx={{ mt: .25 }}>{icon}</Box>
      <Box>
        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="caption" color="text.secondary">{body}</Typography>
      </Box>
    </Stack>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint: string }): JSX.Element {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{hint}</Typography>
      </Paper>
    </Grid>
  );
}

function PeriodDetail({ view }: { view?: vatApi.VatPeriodView }): JSX.Element {
  if (!view) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <EmptyState
          icon={<PriceCheck fontSize="inherit" />}
          title="Sin período creado"
          description="Cargá una compra o ajuste para abrir el workspace mensual."
        />
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="subtitle1">Resumen {view.period}</Typography>
            <Typography variant="caption" color="text.secondary">
              {view.fromDate} al {view.toDate}
            </Typography>
          </Box>
          <Chip label={view.status === 'Closed' ? 'Cerrado' : 'Abierto'} color={view.status === 'Closed' ? 'default' : 'success'} />
        </Stack>
        {view.alerts.length > 0 ? (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {view.alerts.map((alert) => (
              <MuiAlert
                key={alert.code}
                severity={alert.severity === 'ActionRequired' ? 'error' : alert.severity === 'Warning' ? 'warning' : 'info'}
              >
                {alert.message}
              </MuiAlert>
            ))}
          </Stack>
        ) : null}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Alícuota</TableCell>
                <TableCell align="right">Ventas netas</TableCell>
                <TableCell align="right">IVA ventas</TableCell>
                <TableCell align="right">Compras netas</TableCell>
                <TableCell align="right">IVA compras</TableCell>
                <TableCell align="right">Crédito computable</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {view.summary.byRate.length === 0 ? (
                <TableRow><TableCell colSpan={6}>Sin movimientos por alícuota.</TableCell></TableRow>
              ) : view.summary.byRate.map((row) => (
                <TableRow key={row.rate}>
                  <TableCell>{IVA_RATE_LABEL[row.rate]}</TableCell>
                  <TableCell align="right">{formatCurrency(row.salesNet, 'ARS')}</TableCell>
                  <TableCell align="right">{formatCurrency(row.salesVat, 'ARS')}</TableCell>
                  <TableCell align="right">{formatCurrency(row.purchasesNet, 'ARS')}</TableCell>
                  <TableCell align="right">{formatCurrency(row.purchasesVat, 'ARS')}</TableCell>
                  <TableCell align="right">{formatCurrency(row.computableInputVat, 'ARS')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <MovementTables view={view} />
    </Stack>
  );
}

function MovementTables({ view }: { view: vatApi.VatPeriodView }): JSX.Element {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>Compras y ajustes</Typography>
      <Typography variant="subtitle2" sx={{ mt: 1 }}>Compras</Typography>
      <TableContainer sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fecha</TableCell>
              <TableCell>Proveedor</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Crédito computable</TableCell>
              <TableCell>Tratamiento</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {view.purchases.length === 0 ? (
              <TableRow><TableCell colSpan={6}>Sin compras cargadas.</TableCell></TableRow>
            ) : view.purchases.map((purchase) => (
              <TableRow key={purchase.id}>
                <TableCell>{purchase.issueDate}</TableCell>
                <TableCell>{purchase.supplierName} · {purchase.supplierCuit}</TableCell>
                <TableCell>{PURCHASE_KIND_LABEL[purchase.kind]}</TableCell>
                <TableCell align="right">{formatCurrency(purchase.totalAmount, purchase.currency)}</TableCell>
                <TableCell align="right">{formatCurrency(purchase.computableCreditFiscal, purchase.currency)}</TableCell>
                <TableCell>{CREDIT_TREATMENT_LABEL[purchase.creditFiscalTreatment]}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 1 }} />
      <Typography variant="subtitle2">Ajustes</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fecha</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Impuesto</TableCell>
              <TableCell align="right">Importe</TableCell>
              <TableCell>Certificado / descripción</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {view.adjustments.length === 0 ? (
              <TableRow><TableCell colSpan={5}>Sin ajustes cargados.</TableCell></TableRow>
            ) : view.adjustments.map((adjustment) => (
              <TableRow key={adjustment.id}>
                <TableCell>{adjustment.effectiveDate}</TableCell>
                <TableCell>{ADJUSTMENT_KIND_LABEL[adjustment.kind]}</TableCell>
                <TableCell>{TAX_LABEL[adjustment.tax]}</TableCell>
                <TableCell align="right">{formatCurrency(adjustment.amount, 'ARS')}</TableCell>
                <TableCell>{adjustment.certificateNumber ?? adjustment.description ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function PurchaseForm({
  form,
  disabled,
  onChange,
  onSubmit,
}: {
  form: PurchaseFormState;
  disabled: boolean;
  onChange: (patch: Partial<PurchaseFormState>) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}): JSX.Element {
  return (
    <Paper component="form" variant="outlined" sx={{ p: 2 }} onSubmit={onSubmit}>
      <Typography variant="subtitle1" gutterBottom>Cargar compra</Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={6}><TextField fullWidth required size="small" label="Fecha" type="date" value={form.issueDate} onChange={(e) => onChange({ issueDate: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
        <Grid item xs={6}><TextField fullWidth select size="small" label="Tipo" value={form.kind} onChange={(e) => onChange({ kind: e.target.value as vatApi.VatPurchaseDocumentKind })}>{Object.entries(PURCHASE_KIND_LABEL).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField></Grid>
        <Grid item xs={6}><TextField fullWidth required size="small" label="CUIT proveedor" value={form.supplierCuit} onChange={(e) => onChange({ supplierCuit: e.target.value })} /></Grid>
        <Grid item xs={6}><TextField fullWidth required size="small" label="Proveedor" value={form.supplierName} onChange={(e) => onChange({ supplierName: e.target.value })} /></Grid>
        <Grid item xs={4}><TextField fullWidth required size="small" label="Pto. vta." value={form.pointOfSale} onChange={(e) => onChange({ pointOfSale: e.target.value })} /></Grid>
        <Grid item xs={8}><TextField fullWidth required size="small" label="Número" value={form.documentNumber} onChange={(e) => onChange({ documentNumber: e.target.value })} /></Grid>
        <Grid item xs={6}><TextField fullWidth required size="small" label="Moneda" value={form.currency} onChange={(e) => onChange({ currency: e.target.value.toUpperCase() })} /></Grid>
        <Grid item xs={6}><TextField fullWidth required size="small" label="Tipo de cambio a ARS" value={form.exchangeRate} onChange={(e) => onChange({ exchangeRate: e.target.value })} /></Grid>
        <Grid item xs={4}><TextField fullWidth required size="small" label="Neto gravado" value={form.baseAmount} onChange={(e) => onChange({ baseAmount: e.target.value })} /></Grid>
        <Grid item xs={4}><TextField fullWidth select size="small" label="Alícuota" value={form.rate} onChange={(e) => onChange({ rate: e.target.value as IvaRate })}>{IVA_RATES.map((rate) => <MenuItem key={rate} value={rate}>{IVA_RATE_LABEL[rate]}</MenuItem>)}</TextField></Grid>
        <Grid item xs={4}><TextField fullWidth required size="small" label="IVA" value={form.vatAmount} onChange={(e) => onChange({ vatAmount: e.target.value })} /></Grid>
        <Grid item xs={4}><TextField fullWidth required size="small" label="Total" value={form.totalAmount} onChange={(e) => onChange({ totalAmount: e.target.value })} /></Grid>
        <Grid item xs={4}><TextField fullWidth size="small" label="No gravado" value={form.nonTaxedAmount} onChange={(e) => onChange({ nonTaxedAmount: e.target.value })} /></Grid>
        <Grid item xs={4}><TextField fullWidth size="small" label="Exento" value={form.exemptAmount} onChange={(e) => onChange({ exemptAmount: e.target.value })} /></Grid>
        <Grid item xs={7}><TextField fullWidth select size="small" label="Crédito fiscal" value={form.creditFiscalTreatment} onChange={(e) => onChange({ creditFiscalTreatment: e.target.value as vatApi.VatCreditFiscalTreatment })}>{Object.entries(CREDIT_TREATMENT_LABEL).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField></Grid>
        <Grid item xs={5}><TextField fullWidth size="small" label="% computable" value={form.creditFiscalPercentage} onChange={(e) => onChange({ creditFiscalPercentage: e.target.value })} /></Grid>
        <Grid item xs={12}><TextField fullWidth size="small" label="Referencia externa" value={form.externalReference} onChange={(e) => onChange({ externalReference: e.target.value })} /></Grid>
      </Grid>
      <Button type="submit" startIcon={<Save />} variant="contained" sx={{ mt: 2 }} disabled={disabled}>Guardar compra</Button>
    </Paper>
  );
}

function ImportCsvForm({
  csv,
  sampleCsv,
  disabled,
  onChange,
  onSubmit,
}: {
  csv: string;
  sampleCsv: string;
  disabled: boolean;
  onChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}): JSX.Element {
  return (
    <Paper component="form" variant="outlined" sx={{ p: 2 }} onSubmit={onSubmit}>
      <Typography variant="subtitle1" gutterBottom>Importar compras CSV</Typography>
      <TextField
        fullWidth
        multiline
        minRows={4}
        size="small"
        value={csv}
        onChange={(e) => onChange(e.target.value)}
        placeholder={sampleCsv}
        helperText="Headers esperados: issueDate, supplierCuit, supplierName, kind, pointOfSale, documentNumber, totalAmount, rate21Base, rate21Vat…"
      />
      <Button type="submit" startIcon={<UploadFile />} variant="outlined" sx={{ mt: 2 }} disabled={disabled || !csv.trim()}>Importar CSV</Button>
    </Paper>
  );
}

function AdjustmentForm({
  form,
  disabled,
  onChange,
  onSubmit,
}: {
  form: AdjustmentFormState;
  disabled: boolean;
  onChange: (patch: Partial<AdjustmentFormState>) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}): JSX.Element {
  return (
    <Paper component="form" variant="outlined" sx={{ p: 2 }} onSubmit={onSubmit}>
      <Typography variant="subtitle1" gutterBottom>Registrar ajuste</Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={6}><TextField fullWidth required size="small" label="Fecha" type="date" value={form.effectiveDate} onChange={(e) => onChange({ effectiveDate: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
        <Grid item xs={6}><TextField fullWidth select size="small" label="Impuesto" value={form.tax} onChange={(e) => onChange({ tax: e.target.value as vatApi.VatTaxKind })}>{Object.entries(TAX_LABEL).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField></Grid>
        <Grid item xs={12}><TextField fullWidth select size="small" label="Tipo de ajuste" value={form.kind} onChange={(e) => onChange({ kind: e.target.value as vatApi.VatAdjustmentKind })}>{Object.entries(ADJUSTMENT_KIND_LABEL).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField></Grid>
        <Grid item xs={4}><TextField fullWidth required size="small" label="Importe" value={form.amount} onChange={(e) => onChange({ amount: e.target.value })} /></Grid>
        <Grid item xs={4}><TextField fullWidth size="small" label="Base" value={form.taxBase} onChange={(e) => onChange({ taxBase: e.target.value })} /></Grid>
        <Grid item xs={4}><TextField fullWidth size="small" label="%" value={form.rate} onChange={(e) => onChange({ rate: e.target.value })} /></Grid>
        <Grid item xs={12}><TextField fullWidth size="small" label="Certificado" value={form.certificateNumber} onChange={(e) => onChange({ certificateNumber: e.target.value })} /></Grid>
        <Grid item xs={12}><TextField fullWidth size="small" label="Descripción" value={form.description} onChange={(e) => onChange({ description: e.target.value })} /></Grid>
        <Grid item xs={12}><TextField fullWidth size="small" label="Referencia externa" value={form.externalReference} onChange={(e) => onChange({ externalReference: e.target.value })} /></Grid>
      </Grid>
      <Button type="submit" startIcon={<Save />} variant="contained" sx={{ mt: 2 }} disabled={disabled}>Guardar ajuste</Button>
    </Paper>
  );
}

function ExportPreview({ exported }: { exported: vatApi.VatPeriodExport }): JSX.Element {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>Export {exported.period}</Typography>
      <Grid container spacing={2}>
        <CsvBox label="Ventas" value={exported.salesCsv} />
        <CsvBox label="Compras" value={exported.purchasesCsv} />
        <CsvBox label="Ajustes" value={exported.adjustmentsCsv} />
        <CsvBox label="Resumen" value={exported.summaryCsv} />
      </Grid>
    </Paper>
  );
}

function CsvBox({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        multiline
        minRows={6}
        label={label}
        value={value}
        InputProps={{ readOnly: true }}
      />
    </Grid>
  );
}
